import React, { useEffect, useRef, useState } from "react";
import { Warning, X } from "@phosphor-icons/react";
import { trackAnalytics } from "src/pages/helpers";
import { EVENT_NAMES, EVENT_PARAMETERS, BANNER_TYPES } from "src/utils/EventConstants";

export const DEFAULT_OUTAGE_MESSAGE =
  "We're experiencing technical issues — some chats may fail to regenerate. Please try again in about 2 hours.";

/**
 * App-wide, non-dismissible incident notice.
 *
 * DEFAULT-ON: this ships during an active incident, so it renders unless
 * REACT_APP_SERVICE_OUTAGE is explicitly "false". Set that env var (or revert
 * this commit) once the outage clears.
 *
 * Note: CRA inlines process.env at build time, so flipping the flag needs a redeploy.
 */
const ServiceOutageBanner = () => {
  const isOutage = process.env.REACT_APP_SERVICE_OUTAGE !== "false";
  const hasTracked = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isOutage || hasTracked.current) return;
    hasTracked.current = true;
    trackAnalytics(EVENT_NAMES.VIEWED_BANNER, {
      [EVENT_PARAMETERS.BANNER_TYPE]: BANNER_TYPES.SERVICE_OUTAGE,
    });
  }, [isOutage]);

  if (!isOutage || dismissed) return null;

  const message = process.env.REACT_APP_SERVICE_OUTAGE_MESSAGE || DEFAULT_OUTAGE_MESSAGE;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-1.5 px-2 py-1 bg-[#B45309] text-white text-xs sm:gap-2 sm:!px-4 sm:!py-2.5 sm:text-[14px]"
    >
      <Warning size={16} weight="fill" className="flex-shrink-0 w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />
      <span className="flex-1 text-center">{message}</span>
      <button
        onClick={() => {
          setDismissed(true);
          trackAnalytics(EVENT_NAMES.CLOSED_BANNER, {
            [EVENT_PARAMETERS.BANNER_TYPE]: BANNER_TYPES.SERVICE_OUTAGE,
          });
        }}
        className="flex-shrink-0 p-0.5 rounded hover:bg-white/20 transition-colors"
        aria-label="Dismiss banner"
      >
        <X size={14} weight="bold" className="sm:w-4 sm:h-4" />
      </button>
    </div>
  );
};

export default ServiceOutageBanner;
